import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const control =
  "w-full rounded-xl border bg-white px-3.5 py-2.5 text-[15px] text-brand-anthracite placeholder:text-slate-400 transition-[border-color,box-shadow] focus:border-brand-blue focus:outline-none focus:ring-4 focus:ring-brand-blue/15";
const state = (invalid) => (invalid ? "border-red-400 bg-red-50/40" : "border-slate-200 hover:border-slate-300");

export const Field = ({ label, htmlFor, required, error, hint, className, children }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <label htmlFor={htmlFor} className="block text-[13px] font-semibold text-brand-anthracite">
        {label}
        {required ? <span className="text-brand-blue"> *</span> : <span className="font-normal text-slate-500"> (optional)</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
    {error && (
      <p role="alert" className="text-xs font-semibold text-red-600" data-testid={`${htmlFor}-error`}>
        {error}
      </p>
    )}
  </div>
);

export const TextInput = forwardRef(({ invalid, className, ...props }, ref) => (
  <input ref={ref} aria-invalid={invalid || undefined} className={cn(control, state(invalid), className)} {...props} />
));
TextInput.displayName = "TextInput";

export const TextArea = forwardRef(({ invalid, className, ...props }, ref) => (
  <textarea ref={ref} aria-invalid={invalid || undefined} className={cn(control, "min-h-[110px] resize-y", state(invalid), className)} {...props} />
));
TextArea.displayName = "TextArea";

export const SelectInput = forwardRef(({ invalid, className, children, ...props }, ref) => (
  <div className="relative">
    <select ref={ref} aria-invalid={invalid || undefined} className={cn(control, "appearance-none pr-10", state(invalid), className)} {...props}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
  </div>
));
SelectInput.displayName = "SelectInput";

export const Segmented = ({ name, options, register, prefix, cols = 3 }) => (
  <div className={cn("grid gap-2", cols === 2 ? "grid-cols-2" : "grid-cols-3")}>
    {options.map((o) => (
      <label key={o.value} className="cursor-pointer" data-testid={`${prefix}-${o.value}`}>
        <input type="radio" value={o.value} {...register(name)} className="peer sr-only" data-testid={`${prefix}-${o.value}-input`} />
        <span className="block rounded-xl border border-slate-200 px-2 py-2.5 text-center text-sm font-semibold text-brand-ink transition-colors hover:border-slate-300 peer-checked:border-brand-blue peer-checked:bg-brand-sky-light peer-checked:text-brand-blue peer-focus-visible:ring-4 peer-focus-visible:ring-brand-blue/20">
          {o.label}
        </span>
      </label>
    ))}
  </div>
);

export const ConsentField = ({ id, register, error }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-snug text-brand-ink">
      <input id={id} type="checkbox" {...register("consent")} data-testid={id} className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-[#0071B9]" />
      <span>
        Ich willige ein, dass meine Angaben (ggf. inkl. Fotos) zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden. Widerruf jederzeit möglich. Mehr in der{" "}
        <Link to="/datenschutz" className="font-semibold text-brand-blue underline underline-offset-2" data-testid={`${id}-privacy-link`}>
          Datenschutzerklärung
        </Link>
        . <span className="text-brand-blue">*</span>
      </span>
    </label>
    {error && <p role="alert" className="text-xs font-semibold text-red-600" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const Honeypot = ({ register }) => (
  <div aria-hidden="true" className="pointer-events-none absolute -left-[9999px] top-0 h-px w-px overflow-hidden opacity-0">
    <label>
      Website
      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
    </label>
  </div>
);

export const FormError = ({ error, testId }) =>
  error ? (
    <div role="alert" data-testid={testId} className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
    </div>
  ) : null;

export const SubmitButton = ({ loading, progress, testId, children }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand-blue px-5 py-3.5 text-[15px] font-bold text-white shadow-soft transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-blue-dark disabled:cursor-wait disabled:opacity-75 disabled:hover:translate-y-0"
  >
    {loading ? (
      <>
        <Loader2 className="h-4 w-4 animate-spin" /> {progress > 0 && progress < 100 ? `Wird hochgeladen … ${progress} %` : "Wird gesendet …"}
      </>
    ) : (
      <>
        {children} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </>
    )}
  </button>
);
